// What-If Scenarios & Strategic Decision Support — interactive demo.
// Dark "deep-tech" full-screen experience opened from Finance → Cash & Forecast → Forecast:
// decision tree on live financials, editable assumptions, explainable metrics,
// condition stress-testing, golden recommendation, comments, action plan,
// and the floating Digital Finance Employee orb.
import { useEffect, useMemo, useRef, useState } from "react";
import posthog from "@/lib/posthog";
import { Icon } from "@/components/Icon";

/* ═══ Financial model ═══════════════════════════════════════════════ */
const BASE = { cash: 184000, revenue: 95000, expenses: 105200, margin: 0.62 };
const BASE_BURN = BASE.expenses - BASE.revenue; // $10,200/mo
const BASE_RUNWAY = BASE.cash / BASE_BURN; // ~18 mo

interface Conditions { interest: number; market: number; price: number }
const COND0: Conditions = { interest: 4.5, market: 0, price: 0 };

interface FieldDef { key: string; label: string; kind: "usd" | "pct" | "mo"; def: number; src: string }
interface BranchDef { id: string; name: string; sub: string; icon: string; fields: FieldDef[] }

const BRANCHES: BranchDef[] = [
  {
    id: "hire", name: "In-house marketing hire", sub: "Full-time senior marketer on payroll", icon: "user",
    fields: [
      { key: "salary", label: "Annual salary", kind: "usd", def: 95000, src: "Benchmark · US senior marketer median" },
      { key: "load", label: "Payroll taxes & benefits", kind: "pct", def: 28, src: "Your QuickBooks payroll actuals" },
      { key: "equipment", label: "Equipment & software (one-time)", kind: "usd", def: 6000, src: "Editable assumption" },
      { key: "ramp", label: "Ramp-up time", kind: "mo", def: 4, src: "Benchmark · time to full productivity" },
      { key: "lift", label: "Monthly revenue lift at full ramp", kind: "usd", def: 22000, src: "AI estimate from your pipeline data" },
    ],
  },
  {
    id: "agency", name: "External marketing agency", sub: "Retainer engagement, senior team day one", icon: "users",
    fields: [
      { key: "retainer", label: "Monthly retainer", kind: "usd", def: 8500, src: "Benchmark · mid-market agency retainers" },
      { key: "onboarding", label: "Onboarding fee (one-time)", kind: "usd", def: 5000, src: "Editable assumption" },
      { key: "ramp", label: "Ramp-up time", kind: "mo", def: 2, src: "Benchmark · agency time-to-impact" },
      { key: "lift", label: "Monthly revenue lift at full ramp", kind: "usd", def: 18000, src: "AI estimate from your pipeline data" },
    ],
  },
  {
    id: "paid", name: "Focused paid social project", sub: "6-month performance campaign", icon: "zap",
    fields: [
      { key: "media", label: "Monthly media budget", kind: "usd", def: 12000, src: "Editable assumption" },
      { key: "creative", label: "Creative & production (one-time)", kind: "usd", def: 8000, src: "Editable assumption" },
      { key: "campaign", label: "Campaign length", kind: "mo", def: 6, src: "Editable assumption" },
      { key: "lift", label: "Monthly revenue lift during campaign", kind: "usd", def: 15000, src: "AI estimate · channel benchmarks" },
      { key: "residual", label: "Residual lift after campaign", kind: "pct", def: 35, src: "Benchmark · post-campaign retention" },
    ],
  },
];

const defaultAssumps = () =>
  Object.fromEntries(BRANCHES.map((b) => [b.id, Object.fromEntries(b.fields.map((f) => [f.key, f.def]))]));

interface ScenState {
  assumps: Record<string, Record<string, number>>;
  cond: Conditions;
  overrides: Record<string, number>; // "branchId:metric" → pinned value
}
const initialState = (): ScenState => ({ assumps: defaultAssumps(), cond: { ...COND0 }, overrides: {} });
const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v));

interface ExplainRow { label: string; value: string; src: string }
interface Explain { formula: string; inputs: ExplainRow[]; note: string }
export interface BranchResult {
  cost12: number; lift12: number; gross12: number; net: number; roi: number;
  breakeven: number | null; burnNew: number; runwayNew: number; cashCurve: number[];
  explain: Record<string, Explain>;
}

const condMult = (c: Conditions) =>
  (1 + c.market / 100) * (1 + (c.price / 100) * 0.6) * (1 - (c.interest - COND0.interest) * 0.015);

function computeBranch(def: BranchDef, a: Record<string, number>, cond: Conditions): BranchResult {
  const mult = condMult(cond);
  const cost: number[] = []; const lift: number[] = [];
  for (let m = 1; m <= 12; m++) {
    let c = 0; let l = 0;
    if (def.id === "hire") {
      c = (a.salary * (1 + a.load / 100)) / 12 + (m === 1 ? a.equipment : 0);
      l = a.lift * Math.min(1, m / Math.max(1, a.ramp));
    } else if (def.id === "agency") {
      c = a.retainer + (m === 1 ? a.onboarding : 0);
      l = a.lift * Math.min(1, m / Math.max(1, a.ramp));
    } else {
      c = (m <= a.campaign ? a.media : 0) + (m === 1 ? a.creative : 0);
      l = m <= a.campaign ? a.lift : a.lift * (a.residual / 100);
    }
    cost.push(c); lift.push(l * mult);
  }
  const cost12 = cost.reduce((s, v) => s + v, 0);
  const lift12 = lift.reduce((s, v) => s + v, 0);
  const gross12 = lift12 * BASE.margin;
  const net = gross12 - cost12;
  const roi = cost12 > 0 ? net / cost12 : 0;
  let breakeven: number | null = null; let cc = 0; let cg = 0;
  for (let m = 0; m < 12; m++) { cc += cost[m]; cg += lift[m] * BASE.margin; if (breakeven === null && cg >= cc) breakeven = m + 1; }
  const burnNew = BASE_BURN + cost12 / 12 - gross12 / 12;
  const runwayNew = burnNew <= 200 ? 36 : Math.min(36, BASE.cash / burnNew);
  const cashCurve = [BASE.cash];
  for (let m = 0; m < 12; m++) cashCurve.push(cashCurve[m] - BASE_BURN - cost[m] + lift[m] * BASE.margin);

  const fieldRows: ExplainRow[] = def.fields.map((f) => ({
    label: f.label,
    value: f.kind === "usd" ? fmtUsd(a[f.key]) : f.kind === "pct" ? a[f.key] + "%" : a[f.key] + " mo",
    src: f.src,
  }));
  const condRow: ExplainRow = {
    label: "Condition multiplier", value: "×" + mult.toFixed(3),
    src: `Interest ${cond.interest}% · market ${cond.market >= 0 ? "+" : ""}${cond.market}% · price ${cond.price >= 0 ? "+" : ""}${cond.price}%`,
  };
  const costFormula =
    def.id === "hire" ? `(Salary × ${(1 + a.load / 100).toFixed(2)} payroll load) + one-time equipment = ${fmtUsd(cost12)}`
      : def.id === "agency" ? `(Retainer × 12 months) + onboarding fee = ${fmtUsd(cost12)}`
        : `(Media budget × ${a.campaign} campaign months) + creative production = ${fmtUsd(cost12)}`;
  const explain: Record<string, Explain> = {
    cost12: {
      formula: costFormula, inputs: fieldRows,
      note: "Total cash out over 12 months. Every input is an editable assumption — change it and press Run Scenarios to recalculate the whole tree.",
    },
    lift12: {
      formula: `Σ (monthly lift × ramp factor × condition multiplier) = ${fmtUsd(lift12)}`,
      inputs: [...fieldRows, condRow],
      note: "Revenue lift ramps in over the first months, then is stress-tested against your macro conditions. A 1-point interest-rate rise trims projected demand by 1.5%.",
    },
    roi: {
      formula: `(Gross profit lift ${fmtUsd(gross12)} − total cost ${fmtUsd(cost12)}) ÷ total cost = ${fmtPct(roi)}`,
      inputs: [
        { label: "Gross margin applied", value: Math.round(BASE.margin * 100) + "%", src: "Your P&L actuals, trailing 6 months" },
        condRow,
      ],
      note: "ROI uses gross profit on the new revenue, not top-line revenue — new sales still carry cost of delivery.",
    },
    breakeven: {
      formula: `First month where cumulative gross profit lift ≥ cumulative cost → ${breakeven ? "month " + breakeven : "beyond 12 months"}`,
      inputs: fieldRows,
      note: "Earlier break-even means less cash at risk if the plan underperforms and you need to reverse course.",
    },
    runway: {
      formula: `Cash ${fmtUsd(BASE.cash)} ÷ new net burn ${fmtUsd(burnNew)}/mo = ${fmtMo(runwayNew)}`,
      inputs: [
        { label: "Current cash on hand", value: fmtUsd(BASE.cash), src: "Live bank feed · 3 accounts" },
        { label: "Base net burn", value: fmtUsd(BASE_BURN) + "/mo", src: "Your P&L actuals" },
        { label: "Added monthly cost", value: fmtUsd(cost12 / 12) + "/mo", src: "This branch, averaged" },
        { label: "Added gross profit", value: fmtUsd(gross12 / 12) + "/mo", src: "This branch, averaged" },
      ],
      note: "How long your cash lasts if this path is executed and everything else holds steady.",
    },
  };
  return { cost12, lift12, gross12, net, roi, breakeven, burnNew, runwayNew, cashCurve, explain };
}

/* ═══ Formatting ════════════════════════════════════════════════════ */
function fmtUsd(n: number, sign = false) {
  const s = n < 0 ? "-" : sign ? "+" : "";
  return s + "$" + Math.round(Math.abs(n)).toLocaleString("en-US");
}
const fmtPct = (n: number) => (n * 100).toFixed(1) + "%";
const fmtMo = (n: number) => (n >= 36 ? "36+ mo" : n.toFixed(1) + " mo");

const METRICS: { key: string; label: string; get: (r: BranchResult) => string }[] = [
  { key: "cost12", label: "12-mo cost", get: (r) => fmtUsd(r.cost12) },
  { key: "lift12", label: "Revenue lift", get: (r) => fmtUsd(r.lift12) },
  { key: "roi", label: "ROI", get: (r) => fmtPct(r.roi) },
  { key: "breakeven", label: "Break-even", get: (r) => (r.breakeven ? "Mo " + r.breakeven : ">12 mo") },
];

/* ═══ Screen ════════════════════════════════════════════════════════ */
interface CommentItem { who: string; text: string; time: string }
interface Version { name: string; state: ScenState }
interface ToastItem { id: number; text: string }

export const WhatIfScreen = ({ onBack }: { onBack: () => void }) => {
  const [draft, setDraft] = useState<ScenState>(initialState);
  const [committed, setCommitted] = useState<ScenState>(initialState);
  const [versions, setVersions] = useState<Version[]>([{ name: "Original plan", state: initialState() }]);
  const [verIdx, setVerIdx] = useState(0);
  const [running, setRunning] = useState(false);
  const [runKey, setRunKey] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [explainSel, setExplainSel] = useState<{ bid: string; metric: string } | null>(null);
  const [condOpen, setCondOpen] = useState(true);
  const [planFor, setPlanFor] = useState<string | null>(null);
  const [comments, setComments] = useState<Record<string, CommentItem[]>>({
    hire: [{ who: "Marcus · Client", text: "I like owning this capability in-house long-term.", time: "1h ago" }],
    agency: [{ who: "Dana · CFO", text: "Agency gives us senior talent day one — but let's check the exit terms.", time: "2h ago" }],
    paid: [],
  });
  const [commentDraft, setCommentDraft] = useState("");
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [orbState, setOrbState] = useState<"idle" | "listening">("idle");
  const [suggestion, setSuggestion] = useState(false);
  const toastId = useRef(0);
  const suggestTimer = useRef<ReturnType<typeof setTimeout>>();

  const toast = (text: string) => {
    const id = ++toastId.current;
    setToasts((t) => [...t, { id, text }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3600);
  };

  const results = useMemo(() => {
    const map: Record<string, BranchResult> = {};
    for (const b of BRANCHES) map[b.id] = computeBranch(b, committed.assumps[b.id], committed.cond);
    return map;
  }, [committed]);

  const goldenId = useMemo(() => {
    let best = BRANCHES[0].id; let bestScore = -Infinity;
    for (const b of BRANCHES) {
      const r = results[b.id];
      const score = r.net + (r.runwayNew - BASE_RUNWAY) * 2500 + (r.breakeven ? (13 - r.breakeven) * 800 : -3000);
      if (score > bestScore) { bestScore = score; best = b.id; }
    }
    return best;
  }, [results]);

  const sel = selected ?? goldenId;
  const selDef = BRANCHES.find((b) => b.id === sel)!;
  const selRes = results[sel];
  const golden = BRANCHES.find((b) => b.id === goldenId)!;
  const gRes = results[goldenId];
  const stale = JSON.stringify(draft) !== JSON.stringify(committed);

  const runScenarios = (next?: ScenState) => {
    const target = next ?? draft;
    posthog.capture("whatif_run", { conditions: target.cond, overrides: Object.keys(target.overrides).length });
    setRunning(true);
    setTimeout(() => {
      setCommitted(clone(target));
      if (next) setDraft(clone(target));
      setRunning(false);
      setRunKey((k) => k + 1);
      toast("Scenario tree recalculated — AI insights updated");
    }, 750);
  };

  const saveAsNew = () => {
    const name = `Strategy option ${versions.length + 1}`;
    posthog.capture("whatif_saved_as_project", { name });
    setVersions((v) => [...v, { name, state: clone(draft) }]);
    setVerIdx(versions.length);
    toast(`Saved as new project: "${name}" — original preserved`);
  };

  const loadVersion = (i: number) => {
    posthog.capture("whatif_version_loaded", { name: versions[i].name });
    setVerIdx(i);
    setDraft(clone(versions[i].state));
    setCommitted(clone(versions[i].state));
    setExplainSel(null);
    toast(`Loaded "${versions[i].name}"`);
  };

  const setAssump = (bid: string, key: string, val: number) =>
    setDraft((d) => ({ ...d, assumps: { ...d.assumps, [bid]: { ...d.assumps[bid], [key]: val } } }));
  const setCond = (key: keyof Conditions, val: number) =>
    setDraft((d) => ({ ...d, cond: { ...d.cond, [key]: val } }));
  const setOverride = (bid: string, metric: string, val: number | null) =>
    setDraft((d) => {
      const o = { ...d.overrides };
      if (val === null) delete o[`${bid}:${metric}`]; else o[`${bid}:${metric}`] = val;
      return { ...d, overrides: o };
    });

  const activateOrb = () => {
    posthog.capture("whatif_orb_toggled", { to: orbState === "listening" ? "idle" : "listening" });
    if (orbState === "listening") {
      setOrbState("idle"); setSuggestion(false);
      if (suggestTimer.current) clearTimeout(suggestTimer.current);
      toast("AI Employee stopped listening — session saved to your private log");
      return;
    }
    setOrbState("listening");
    toast("AI Employee is listening & learning — all participants notified");
    suggestTimer.current = setTimeout(() => setSuggestion(true), 6000);
  };
  useEffect(() => () => { if (suggestTimer.current) clearTimeout(suggestTimer.current); }, []);

  const approveSuggestion = () => {
    posthog.capture("whatif_ai_suggestion_approved", { suggestion: "interest_rate_stress_test" });
    setSuggestion(false);
    const next = clone(draft);
    next.cond.interest = Math.min(12, next.cond.interest + 2);
    runScenarios(next);
    toast("Approved — running a +2% interest-rate stress test");
  };

  const addComment = () => {
    if (!commentDraft.trim()) return;
    posthog.capture("whatif_comment_added", { branch: sel });
    setComments((c) => ({ ...c, [sel]: [...(c[sel] || []), { who: "You", text: commentDraft.trim(), time: "now" }] }));
    setCommentDraft("");
  };

  const share = () => {
    posthog.capture("whatif_shared");
    navigator.clipboard?.writeText(window.location.href + "#whatif/marketing-investment").catch(() => undefined);
    toast("Interactive share link copied — client gets view & comment access");
  };

  const exportCsv = () => {
    posthog.capture("whatif_exported");
    const rows = [["Branch", "12-mo cost (USD)", "Revenue lift (USD)", "Gross profit lift (USD)", "Net impact (USD)", "ROI", "Break-even", "Runway (mo)"]];
    for (const b of BRANCHES) {
      const r = results[b.id];
      rows.push([b.name, String(Math.round(r.cost12)), String(Math.round(r.lift12)), String(Math.round(r.gross12)),
        String(Math.round(r.net)), fmtPct(r.roi), r.breakeven ? "Month " + r.breakeven : ">12 mo", r.runwayNew.toFixed(1)]);
    }
    const blob = new Blob([rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "what-if-scenarios.csv"; a.click();
    URL.revokeObjectURL(a.href);
    toast("Exported scenario data — what-if-scenarios.csv");
  };

  const explain = explainSel ? results[explainSel.bid]?.explain[explainSel.metric] : null;
  const explainDef = explainSel ? BRANCHES.find((b) => b.id === explainSel.bid) : null;

  return (
    <div className="wi-root fade-up">
      <style>{WI_CSS}</style>

      {orbState === "listening" && (
        <div className="wi-listen-banner">
          <span className="wi-listen-dot" />
          AI Employee is listening to this meeting — learning preferences & recording on-screen actions · consent given by all participants
          <button className="wi-btn wi-btn-ghost" style={{ marginLeft: "auto", height: 26 }} onClick={activateOrb}>Stop</button>
        </div>
      )}

      {/* ── Top bar ── */}
      <div className="wi-top">
        <span className="wi-back" onClick={() => { posthog.capture("whatif_closed"); onBack(); }}>
          <Icon name="chevright" size={13} style={{ transform: "rotate(180deg)" }} /> Cash & Forecast
        </span>
        <span className="wi-divider" />
        <div>
          <div className="wi-title"><Icon name="brain" size={17} stroke="#f5c542" /> What-If Scenarios</div>
          <div className="wi-sub">Marketing Investment Strategy · built from your live financials</div>
        </div>
        <select className="wi-select" value={verIdx} onChange={(e) => loadVersion(Number(e.target.value))}>
          {versions.map((v, i) => <option key={i} value={i}>{v.name}</option>)}
        </select>
        {stale && <span className="wi-stale"><span className="wi-stale-dot" />Edits pending — run to update</span>}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button className="wi-btn" onClick={share}><Icon name="share" size={14} /> Share</button>
          <button className="wi-btn" onClick={exportCsv}><Icon name="download" size={14} /> Export</button>
          <button className="wi-btn" onClick={saveAsNew}><Icon name="bookmark" size={14} /> Save as New Project</button>
          <button className={"wi-btn wi-btn-pri" + (stale ? " glow" : "")} onClick={() => runScenarios()} disabled={running}>
            <Icon name={running ? "refresh" : "play"} size={14} /> {running ? "Recalculating…" : "Run Scenarios"}
          </button>
        </div>
      </div>

      <div className="wi-body">
        {/* ── Conditions rail ── */}
        <div className={"wi-cond" + (condOpen ? "" : " closed")}>
          <div className="wi-cond-head" onClick={() => setCondOpen((o) => !o)}>
            <Icon name="sliders" size={15} stroke="#38BDD3" />
            {condOpen && <span>Conditions</span>}
            <Icon name={condOpen ? "chevright" : "chevright"} size={13} style={{ marginLeft: "auto", transform: condOpen ? "rotate(180deg)" : "none" }} />
          </div>
          {condOpen && <>
            <div className="wi-cond-sub">Stress-test every branch against macro & micro shifts, then run.</div>
            {([
              ["interest", "Interest rate", 0, 12, 0.5, "%"],
              ["market", "Market growth", -20, 20, 1, "%"],
              ["price", "Your price change", -15, 15, 1, "%"],
            ] as const).map(([key, label, min, max, step, unit]) => (
              <div key={key} className="wi-slider">
                <div className="wi-slider-row">
                  <span>{label}</span>
                  <span className="wi-num">{draft.cond[key] > 0 && key !== "interest" ? "+" : ""}{draft.cond[key]}{unit}</span>
                </div>
                <input type="range" min={min} max={max} step={step} value={draft.cond[key]}
                  onChange={(e) => setCond(key, Number(e.target.value))}
                  onPointerUp={() => posthog.capture("whatif_condition_adjusted", { condition: key, value: draft.cond[key] })} />
              </div>
            ))}
            <div className="wi-cond-note">
              Multiplier on projected demand: <b className="wi-num">×{condMult(draft.cond).toFixed(3)}</b>
              <div style={{ marginTop: 6, opacity: 0.7 }}>Each +1pt interest trims demand 1.5% · price changes pass through at 60%.</div>
            </div>
          </>}
        </div>

        {/* ── Canvas ── */}
        <div className="wi-canvas" key={runKey}>
          {/* Root: live financials */}
          <div className="wi-node wi-node-root">
            <div className="wi-node-eyebrow"><span className="wi-live" /> Live financials · QuickBooks + bank feed</div>
            <div className="wi-root-stats">
              <span><b className="wi-num">{fmtUsd(BASE.cash)}</b> cash</span>
              <span><b className="wi-num">{fmtUsd(BASE_BURN)}/mo</b> net burn</span>
              <span><b className="wi-num">{fmtMo(BASE_RUNWAY)}</b> runway</span>
              <span><b className="wi-num">{Math.round(BASE.margin * 100)}%</b> gross margin</span>
            </div>
          </div>
          <svg className="wi-link" viewBox="0 0 300 34" preserveAspectRatio="none">
            <path d="M150 0 L150 34" vectorEffect="non-scaling-stroke" />
          </svg>
          {/* Decision node */}
          <div className="wi-node wi-node-decision">
            <div className="wi-node-eyebrow">Decision</div>
            <div style={{ fontSize: 15.5, fontWeight: 650 }}>How should we invest to grow revenue?</div>
            <div className="wi-sub" style={{ marginTop: 3 }}>3 strategic paths · 12-month horizon · all figures USD</div>
          </div>
          <svg className="wi-link" viewBox="0 0 300 44" preserveAspectRatio="none">
            <path d="M150 0 C150 26 50 18 50 44" vectorEffect="non-scaling-stroke" />
            <path d="M150 0 L150 44" vectorEffect="non-scaling-stroke" />
            <path d="M150 0 C150 26 250 18 250 44" vectorEffect="non-scaling-stroke" />
          </svg>

          {/* Branches */}
          <div className="wi-branches">
            {BRANCHES.map((b) => {
              const r = results[b.id];
              const isGold = b.id === goldenId;
              const isSel = b.id === sel;
              return (
                <div key={b.id} className={"wi-branch" + (isSel ? " sel" : "") + (isGold ? " gold" : "")}
                  onClick={() => { posthog.capture("whatif_branch_selected", { branch: b.id, golden: isGold }); setSelected(b.id); setExplainSel(null); }}>
                  {isGold && <span className="wi-gold-tag"><Icon name="spark" size={11} stroke="#03181D" /> Golden path</span>}
                  <div className="wi-branch-head">
                    <span className="wi-branch-ic"><Icon name={b.icon} size={16} stroke={isGold ? "#f5c542" : "#22d3ee"} /></span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 650 }}>{b.name}</div>
                      <div className="wi-sub">{b.sub}</div>
                    </div>
                    <span className="wi-comments">{(comments[b.id] || []).length > 0 && <><Icon name="comment" size={12} />{comments[b.id].length}</>}</span>
                  </div>
                  <div className="wi-metrics">
                    {METRICS.map((m) => {
                      const ov = committed.overrides[`${b.id}:${m.key}`];
                      return (
                        <button key={m.key} className={"wi-metric" + (explainSel?.bid === b.id && explainSel.metric === m.key ? " on" : "")}
                          onClick={(e) => { e.stopPropagation(); posthog.capture("whatif_metric_explained", { branch: b.id, metric: m.key }); setSelected(b.id); setExplainSel({ bid: b.id, metric: m.key }); }}
                          title="Click to see how this number is calculated">
                          <span className="wi-metric-l">{m.label}{ov != null && <i className="wi-ov-dot" title="Manually overridden" />}</span>
                          <span className={"wi-num wi-metric-v" + ((m.key === "roi" && r.roi < 0) ? " neg" : "")}>
                            {ov != null ? (m.key === "roi" ? fmtPct(ov) : fmtUsd(ov)) : m.get(r)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="wi-branch-foot">
                    <span>Runway {fmtMo(BASE_RUNWAY)} → <b className={r.runwayNew >= BASE_RUNWAY ? "pos" : "neg"}>{fmtMo(r.runwayNew)}</b></span>
                    <span>Net 12-mo <b className={r.net >= 0 ? "pos" : "neg"}>{fmtUsd(r.net, true)}</b></span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Golden recommendation */}
          <div className="wi-golden">
            <div className="wi-golden-head">
              <span className="wi-golden-badge"><Icon name="spark" size={14} stroke="#03181D" /></span>
              <div>
                <div style={{ fontSize: 11, letterSpacing: ".14em", textTransform: "uppercase", color: "#f5c542", fontWeight: 700 }}>Golden Recommendation · AI-generated</div>
                <div style={{ fontSize: 15.5, fontWeight: 650, marginTop: 2 }}>{golden.name}</div>
              </div>
              <button className="wi-btn wi-btn-gold" style={{ marginLeft: "auto" }} onClick={() => { posthog.capture("whatif_path_selected", { branch: goldenId, via: "golden_recommendation" }); setPlanFor(goldenId); }}>
                Select this path <Icon name="arrow-right" size={14} />
              </button>
            </div>
            <div className="wi-golden-body">
              {buildRecommendation(golden, gRes, results)}
            </div>
            <div className="wi-golden-chips">
              <span className="wi-chip">ROI {fmtPct(gRes.roi)}</span>
              <span className="wi-chip">Break-even {gRes.breakeven ? "month " + gRes.breakeven : ">12 mo"}</span>
              <span className="wi-chip">Runway {fmtMo(gRes.runwayNew)}</span>
              <span className="wi-chip">Net impact {fmtUsd(gRes.net, true)}</span>
            </div>
          </div>

          {/* Global impact strip */}
          <div className="wi-strip">
            <div className="wi-strip-label">
              <div style={{ fontSize: 10.5, letterSpacing: ".12em", textTransform: "uppercase", color: "#7FA6B0" }}>Company impact</div>
              <div style={{ fontSize: 12.5, fontWeight: 600, marginTop: 2 }}>if you execute: <span style={{ color: "#22d3ee" }}>{selDef.name}</span></div>
            </div>
            <ImpactTile label="Cash-flow runway" val={fmtMo(selRes.runwayNew)} delta={selRes.runwayNew - BASE_RUNWAY} unit=" mo" />
            <ImpactTile label="Monthly burn" val={fmtUsd(selRes.burnNew) + "/mo"} delta={BASE_BURN - selRes.burnNew} unit="/mo $" money />
            <ImpactTile label="Cash in 12 months" val={fmtUsd(selRes.cashCurve[12])} delta={selRes.cashCurve[12] - (BASE.cash - BASE_BURN * 12)} unit="" money />
            <div className="wi-tile" onClick={() => setExplainSel({ bid: sel, metric: "runway" })} style={{ cursor: "pointer" }}>
              <div className="wi-tile-l">Cash curve · 12 mo</div>
              <CashSpark curve={selRes.cashCurve} />
            </div>
          </div>
        </div>

        {/* ── Right panel ── */}
        <div className="wi-panel">
          {explain && explainDef ? (
            <div className="wi-explain">
              <div className="wi-panel-head">
                <span className="wi-eyebrow" style={{ color: "#22d3ee" }}>How this number is calculated</span>
                <button className="wi-x" onClick={() => setExplainSel(null)}><Icon name="x" size={14} /></button>
              </div>
              <div style={{ fontSize: 13.5, fontWeight: 650, marginBottom: 8 }}>
                {METRICS.find((m) => m.key === explainSel!.metric)?.label ?? "Runway"} · {explainDef.name}
              </div>
              <div className="wi-formula wi-num">{explain.formula}</div>
              <div className="wi-eyebrow" style={{ margin: "12px 0 6px" }}>Inputs & sources</div>
              {explain.inputs.map((r, i) => (
                <div key={i} className="wi-exp-row">
                  <div><div style={{ fontSize: 12.5 }}>{r.label}</div><div className="wi-src">{r.src}</div></div>
                  <span className="wi-num" style={{ fontSize: 12.5, fontWeight: 650 }}>{r.value}</span>
                </div>
              ))}
              <div className="wi-note">{explain.note}</div>
              {explainSel!.metric !== "runway" && (
                <OverrideBox
                  current={committed.overrides[`${explainSel!.bid}:${explainSel!.metric}`]}
                  onSet={(v) => { posthog.capture(v === null ? "whatif_override_cleared" : "whatif_override_pinned", { branch: explainSel!.bid, metric: explainSel!.metric }); setOverride(explainSel!.bid, explainSel!.metric, v); toast(v === null ? "Override cleared — run to recalculate" : "Value pinned — run to propagate"); }}
                />
              )}
            </div>
          ) : (
            <div className="wi-panel-head" style={{ marginBottom: 2 }}>
              <span className="wi-eyebrow">Branch detail</span>
            </div>
          )}

          <div style={{ fontSize: 14, fontWeight: 650, margin: "10px 0 2px" }}>{selDef.name}</div>
          <div className="wi-sub" style={{ marginBottom: 10 }}>{selDef.sub}</div>

          <div className="wi-eyebrow" style={{ marginBottom: 6 }}>Assumptions — edit & run</div>
          {selDef.fields.map((f) => (
            <label key={f.key} className="wi-field">
              <span>
                <span style={{ display: "block", fontSize: 12 }}>{f.label}</span>
                <span className="wi-src">{f.src}</span>
              </span>
              <span className="wi-input">
                {f.kind === "usd" && <i>$</i>}
                <input type="number" value={draft.assumps[sel][f.key]}
                  onChange={(e) => setAssump(sel, f.key, Number(e.target.value) || 0)}
                  onBlur={() => posthog.capture("whatif_assumption_edited", { branch: sel, field: f.key, value: draft.assumps[sel][f.key] })} />
                {f.kind === "pct" && <i>%</i>}{f.kind === "mo" && <i>mo</i>}
              </span>
            </label>
          ))}

          <div className="wi-eyebrow" style={{ margin: "16px 0 6px" }}>Comments</div>
          <div className="wi-comments-list">
            {(comments[sel] || []).map((c, i) => (
              <div key={i} className="wi-comment">
                <div className="wi-comment-head"><b>{c.who}</b><span>{c.time}</span></div>
                <div>{c.text}</div>
              </div>
            ))}
            {(comments[sel] || []).length === 0 && <div className="wi-sub">No comments yet on this branch.</div>}
          </div>
          <div className="wi-comment-box">
            <input placeholder="Comment for your CFO / client…" value={commentDraft}
              onChange={(e) => setCommentDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addComment()} />
            <button className="wi-btn wi-btn-ghost" onClick={addComment}><Icon name="send" size={14} /></button>
          </div>

          <button className="wi-btn wi-btn-pri" style={{ width: "100%", marginTop: 14, justifyContent: "center" }} onClick={() => { posthog.capture("whatif_path_selected", { branch: sel, via: "branch_panel" }); setPlanFor(sel); }}>
            Select this path — generate Next Steps
          </button>
        </div>
      </div>

      {/* ── Action plan modal ── */}
      {planFor && (
        <div className="wi-modal-bg" onClick={() => setPlanFor(null)}>
          <div className="wi-modal" onClick={(e) => e.stopPropagation()}>
            <div className="wi-panel-head">
              <div>
                <div className="wi-eyebrow" style={{ color: "#f5c542" }}>Next Steps · action plan</div>
                <div style={{ fontSize: 16, fontWeight: 650, marginTop: 3 }}>{BRANCHES.find((b) => b.id === planFor)!.name}</div>
              </div>
              <button className="wi-x" onClick={() => setPlanFor(null)}><Icon name="x" size={15} /></button>
            </div>
            <div className="wi-sub" style={{ margin: "4px 0 14px" }}>
              Generated from this scenario's numbers. Edit, assign, and push to your tools — execution is where the plan earns its ROI of {fmtPct(results[planFor].roi)}.
            </div>
            {buildPlan(planFor, results[planFor], committed.assumps[planFor]).map((s, i) => (
              <div key={i} className="wi-step">
                <span className="wi-step-n wi-num">{i + 1}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{s.title}</div>
                  <div className="wi-src">{s.owner} · {s.when}</div>
                </div>
                {s.amount && <span className="wi-num" style={{ fontSize: 12.5, fontWeight: 650, color: "#93B9C2" }}>{s.amount}</span>}
              </div>
            ))}
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button className="wi-btn wi-btn-pri" style={{ flex: 1, justifyContent: "center" }}
                onClick={() => { posthog.capture("whatif_plan_emailed", { branch: planFor }); toast("Action plan emailed to Dana (CFO) and Marcus (client)"); setPlanFor(null); }}>
                <Icon name="send" size={14} /> Send via email
              </button>
              <button className="wi-btn" style={{ flex: 1, justifyContent: "center" }}
                onClick={() => { posthog.capture("whatif_plan_calendar_set", { branch: planFor }); toast("Calendar reminders set for all execution milestones"); setPlanFor(null); }}>
                <Icon name="calendar" size={14} /> Set calendar reminders
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Digital Finance Employee orb ── */}
      <div className="wi-orb-wrap">
        {suggestion && (
          <div className="wi-suggest">
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
              <Icon name="ai" size={15} stroke="#38BDD3" />
              <b style={{ fontSize: 12.5 }}>Your AI Employee · asking permission</b>
            </div>
            <div style={{ fontSize: 12.5, lineHeight: 1.55, color: "#BFD9DE" }}>
              I noticed you usually stress-test plans against rate hikes before deciding. Want me to run a <b>+2% interest-rate</b> shock across all three branches?
            </div>
            <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
              <button className="wi-btn wi-btn-pri" style={{ height: 28 }} onClick={approveSuggestion}>Approve & run</button>
              <button className="wi-btn wi-btn-ghost" style={{ height: 28 }} onClick={() => { posthog.capture("whatif_ai_suggestion_dismissed", { suggestion: "interest_rate_stress_test" }); setSuggestion(false); }}>Not now</button>
            </div>
          </div>
        )}
        <div className="wi-orb-hover">
          <b>Your Digital Finance Employee</b>
          <div>I'm ready to join this meeting — I'll listen, learn how you and your client think, watch the moves you make here, and over time ask permission to run them for you.</div>
          <div style={{ marginTop: 6, color: "#38BDD3", fontWeight: 600 }}>{orbState === "listening" ? "Click to stop listening" : "Click to activate · consent required"}</div>
        </div>
        <button className={"wi-orb" + (orbState === "listening" ? " live" : "")} onClick={activateOrb} aria-label="Digital Finance Employee">
          <Icon name="ai" size={22} stroke="#fff" />
        </button>
      </div>

      {/* ── Toasts ── */}
      <div className="wi-toasts">
        {toasts.map((t) => <div key={t.id} className="wi-toast"><Icon name="check-circle" size={14} stroke="#34d399" />{t.text}</div>)}
      </div>
    </div>
  );
};

/* ═══ Sub-components ════════════════════════════════════════════════ */
const ImpactTile = ({ label, val, delta, unit, money }: { label: string; val: string; delta: number; unit: string; money?: boolean }) => {
  const good = delta >= 0;
  return (
    <div className="wi-tile">
      <div className="wi-tile-l">{label}</div>
      <div className="wi-num wi-tile-v">{val}</div>
      <div className={"wi-tile-d " + (good ? "pos" : "neg")}>
        <Icon name={good ? "arrow-up" : "arrow-down"} size={11} sw={2.4} />
        {money ? fmtUsd(Math.abs(delta)) : Math.abs(delta).toFixed(1) + unit} vs. base plan
      </div>
    </div>
  );
};

const CashSpark = ({ curve }: { curve: number[] }) => {
  const base = Array.from({ length: 13 }, (_, m) => BASE.cash - BASE_BURN * m);
  const all = [...curve, ...base];
  const min = Math.min(...all) * 0.98; const max = Math.max(...all) * 1.02;
  const pt = (arr: number[]) => arr.map((v, i) => `${(i / 12) * 100},${34 - ((v - min) / (max - min)) * 30}`).join(" ");
  return (
    <svg viewBox="0 0 100 36" preserveAspectRatio="none" style={{ width: "100%", height: 36, display: "block" }}>
      <polyline points={pt(base)} fill="none" stroke="rgba(255,255,255,.22)" strokeWidth={1} strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
      <polyline points={pt(curve)} fill="none" stroke="#22d3ee" strokeWidth={1.8} vectorEffect="non-scaling-stroke"
        style={{ filter: "drop-shadow(0 0 4px rgba(34,211,238,.7))" }} />
    </svg>
  );
};

const OverrideBox = ({ current, onSet }: { current?: number; onSet: (v: number | null) => void }) => {
  const [v, setV] = useState<string>(current != null ? String(current) : "");
  return (
    <div className="wi-override">
      <div style={{ fontSize: 11.5, color: "#93B9C2", marginBottom: 6 }}>
        Disagree with the model? Pin your own value — it stays flagged as a manual override.
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <span className="wi-input" style={{ flex: 1 }}>
          <i>$</i><input type="number" placeholder="Your value" value={v} onChange={(e) => setV(e.target.value)} />
        </span>
        <button className="wi-btn wi-btn-ghost" onClick={() => v !== "" && onSet(Number(v))}>Pin</button>
        {current != null && <button className="wi-btn wi-btn-ghost" onClick={() => { setV(""); onSet(null); }}>Reset</button>}
      </div>
    </div>
  );
};

/* ═══ AI text generation (templated from computed numbers) ══════════ */
function buildRecommendation(golden: BranchDef, g: BranchResult, all: Record<string, BranchResult>) {
  const others = BRANCHES.filter((b) => b.id !== golden.id);
  const [a, b] = others.map((o) => ({ def: o, r: all[o.id] }));
  return (
    <>
      <b>{golden.name}</b> is the optimal path on your current numbers: it delivers the strongest 12-month ROI
      at <b className="wi-num">{fmtPct(g.roi)}</b> (vs {fmtPct(a.r.roi)} for {a.def.name.toLowerCase()} and {fmtPct(b.r.roi)} for {b.def.name.toLowerCase()}),
      {" "}breaks even {g.breakeven ? <>by <b>month {g.breakeven}</b></> : <>beyond month 12</>}, and keeps your runway
      at <b className="wi-num">{fmtMo(g.runwayNew)}</b> — comfortably above a 12-month floor.
      Key risk: the projected revenue lift is an estimate — the plan is most sensitive to that single assumption, so
      revisit it after the first 60 days of real data. If conditions tighten (rates up, market softening), re-run this
      tree before committing follow-on spend.
    </>
  );
}

function buildPlan(bid: string, r: BranchResult, a: Record<string, number>) {
  const common = [
    { title: "Review & approve this scenario with your client", owner: "Dana · CFO", when: "This week", amount: undefined as string | undefined },
    { title: "Set a 60-day checkpoint — compare actual lift vs. the " + fmtUsd(r.lift12 / 12) + "/mo projection", owner: "Dana · CFO", when: "Day 60", amount: undefined },
  ];
  if (bid === "hire") return [
    common[0],
    { title: "Post the senior marketer role & brief recruiters", owner: "Marcus · Client", when: "Week 1", amount: fmtUsd(a.salary) + "/yr" },
    { title: "Budget equipment & software stack", owner: "Dana · CFO", when: "Week 2", amount: fmtUsd(a.equipment) },
    { title: "Interview loop & offer target", owner: "Marcus · Client", when: "Weeks 3–6", amount: undefined },
    { title: `Onboarding plan sized to the ${a.ramp}-month ramp`, owner: "Marcus · Client", when: "On start date", amount: undefined },
    common[1],
  ];
  if (bid === "agency") return [
    common[0],
    { title: "Shortlist 3 agencies & request proposals", owner: "Marcus · Client", when: "Week 1", amount: fmtUsd(a.retainer) + "/mo target" },
    { title: "Negotiate contract — cap onboarding & secure 60-day exit clause", owner: "Dana · CFO", when: "Week 2", amount: fmtUsd(a.onboarding) },
    { title: "Kickoff: share brand assets, pipeline data & KPI targets", owner: "Marcus · Client", when: "Week 3", amount: undefined },
    { title: `First performance review vs. the month-${Math.min(4, a.ramp + 2)} break-even track`, owner: "Dana · CFO", when: "Month " + (a.ramp + 1), amount: undefined },
    common[1],
  ];
  return [
    common[0],
    { title: "Approve creative & production budget", owner: "Marcus · Client", when: "Week 1", amount: fmtUsd(a.creative) },
    { title: "Launch campaign with weekly media pacing", owner: "Agency / freelancer", when: "Week 2", amount: fmtUsd(a.media) + "/mo" },
    { title: "Mid-campaign optimization review — kill underperforming ad sets", owner: "Dana · CFO", when: "Month " + Math.ceil(a.campaign / 2), amount: undefined },
    { title: "Decide: extend, scale, or wind down before month " + a.campaign, owner: "Both", when: "Month " + a.campaign, amount: undefined },
    common[1],
  ];
}

/* ═══ Styles ════════════════════════════════════════════════════════ */
const WI_CSS = `
.wi-root{position:relative;background:radial-gradient(1200px 500px at 70% -10%,#084251 0%,#04242B 55%,#031B20 100%);color:#E6F3F5;border-radius:16px;border:1px solid rgba(255,255,255,.07);min-height:760px;overflow:hidden;font-size:13px}
.wi-root .wi-num{font-variant-numeric:tabular-nums}
.wi-root ::selection{background:rgba(56,189,211,.4)}
.wi-eyebrow{font-size:10px;letter-spacing:.14em;text-transform:uppercase;color:#7FA6B0;font-weight:700}
.wi-sub{font-size:11.5px;color:#7FA6B0}
.wi-src{font-size:10.5px;color:#4F757F;display:block;margin-top:1px}
.pos{color:#34d399}.neg{color:#f87171}

.wi-top{display:flex;align-items:center;gap:14px;padding:14px 18px;border-bottom:1px solid rgba(255,255,255,.07);background:rgba(10,10,20,.6);backdrop-filter:blur(6px);position:sticky;top:0;z-index:5;flex-wrap:wrap}
.wi-back{display:inline-flex;align-items:center;gap:5px;font-size:12px;color:#93B9C2;cursor:pointer}
.wi-back:hover{color:#fff}
.wi-divider{width:1px;height:26px;background:rgba(255,255,255,.1)}
.wi-title{display:flex;align-items:center;gap:7px;font-size:15.5px;font-weight:650;letter-spacing:-.01em}
.wi-root select.wi-select{background:#06333F;border:1px solid rgba(255,255,255,.12);color:#E6F3F5;border-radius:8px;height:32px;padding:0 10px;font:inherit;font-size:12px;outline:none;cursor:pointer}
.wi-root select.wi-select option{background:#06333F;color:#E6F3F5}
.wi-stale{display:inline-flex;align-items:center;gap:6px;font-size:11.5px;color:#fbbf24;background:rgba(251,191,36,.08);border:1px solid rgba(251,191,36,.25);border-radius:20px;padding:4px 10px}
.wi-stale-dot{width:6px;height:6px;border-radius:50%;background:#fbbf24;animation:wiPulse 1.4s infinite}

.wi-btn{display:inline-flex;align-items:center;gap:6px;height:32px;padding:0 12px;border-radius:8px;border:1px solid rgba(255,255,255,.14);background:rgba(255,255,255,.04);color:#CFE4E8;font:inherit;font-size:12px;font-weight:600;cursor:pointer;transition:all .15s}
.wi-btn:hover{background:rgba(255,255,255,.09);border-color:rgba(255,255,255,.25)}
.wi-btn-ghost{border-color:transparent;background:transparent}
.wi-btn-pri{background:linear-gradient(135deg,#2CB3C8,#22d3ee);border:none;color:#fff}
.wi-btn-pri:hover{filter:brightness(1.12)}
.wi-btn-pri.glow{box-shadow:0 0 18px rgba(44,179,200,.55),0 0 34px rgba(34,211,238,.25);animation:wiGlow 1.6s infinite alternate}
.wi-btn-pri:disabled{opacity:.6;cursor:default}
.wi-btn-gold{background:linear-gradient(135deg,#f5c542,#e8a020);border:none;color:#041E24}
.wi-btn-gold:hover{filter:brightness(1.08)}
.wi-x{background:none;border:none;color:#7FA6B0;cursor:pointer;padding:4px;border-radius:6px}
.wi-x:hover{color:#fff;background:rgba(255,255,255,.08)}

.wi-listen-banner{display:flex;align-items:center;gap:9px;padding:8px 18px;font-size:12px;color:#D3F0F5;background:linear-gradient(90deg,rgba(56,189,211,.22),rgba(34,211,238,.12));border-bottom:1px solid rgba(56,189,211,.35)}
.wi-listen-dot{width:8px;height:8px;border-radius:50%;background:#6BCEDF;animation:wiPulse 1.2s infinite;flex-shrink:0}

.wi-body{display:grid;grid-template-columns:auto 1fr 320px;gap:0;align-items:start}
.wi-cond{width:236px;padding:16px;border-right:1px solid rgba(255,255,255,.07);position:sticky;top:61px}
.wi-cond.closed{width:52px;padding:16px 10px}
.wi-cond-head{display:flex;align-items:center;gap:8px;font-size:13px;font-weight:650;cursor:pointer;color:#CFE4E8}
.wi-cond-sub{font-size:11px;color:#7FA6B0;margin:8px 0 14px;line-height:1.5}
.wi-slider{margin-bottom:14px}
.wi-slider-row{display:flex;justify-content:space-between;font-size:12px;margin-bottom:5px;color:#BFD9DE}
.wi-slider-row .wi-num{font-weight:650;color:#22d3ee}
.wi-slider input[type=range]{width:100%;accent-color:#38BDD3;background:transparent;cursor:pointer}
.wi-cond-note{font-size:11.5px;color:#93B9C2;background:rgba(56,189,211,.08);border:1px solid rgba(56,189,211,.22);border-radius:10px;padding:10px 12px;line-height:1.5}

.wi-canvas{padding:22px 22px 26px;display:flex;flex-direction:column;align-items:stretch;min-width:0}
.wi-node{border:1px solid rgba(255,255,255,.1);background:rgba(18,18,34,.75);border-radius:13px;padding:13px 16px}
.wi-node-root{align-self:center;min-width:60%;box-shadow:0 0 24px rgba(34,211,238,.09)}
.wi-node-decision{align-self:center;min-width:60%;text-align:center;border-color:rgba(44,179,200,.35);box-shadow:0 0 26px rgba(44,179,200,.14)}
.wi-node-eyebrow{font-size:10px;letter-spacing:.13em;text-transform:uppercase;color:#7FA6B0;font-weight:700;display:flex;align-items:center;justify-content:center;gap:6px;margin-bottom:6px}
.wi-live{width:7px;height:7px;border-radius:50%;background:#34d399;animation:wiPulse 1.6s infinite;display:inline-block}
.wi-root-stats{display:flex;gap:18px;justify-content:center;flex-wrap:wrap;font-size:12px;color:#93B9C2}
.wi-root-stats b{color:#E6F3F5;font-weight:650;margin-right:4px}
.wi-link{width:60%;align-self:center;height:auto;display:block}
.wi-link path{fill:none;stroke:rgba(44,179,200,.55);stroke-width:1.5;stroke-dasharray:5 4;animation:wiFlow 1.2s linear infinite}

.wi-branches{display:grid;grid-template-columns:repeat(3,1fr);gap:14px}
.wi-branch{position:relative;border:1px solid rgba(255,255,255,.1);background:rgba(16,16,30,.85);border-radius:14px;padding:14px;cursor:pointer;transition:border-color .2s,box-shadow .2s,transform .15s}
.wi-branch:hover{transform:translateY(-2px)}
.wi-branch.sel{border-color:rgba(34,211,238,.6);box-shadow:0 0 22px rgba(34,211,238,.18)}
.wi-branch.gold{border-color:rgba(245,197,66,.55);box-shadow:0 0 26px rgba(245,197,66,.16)}
.wi-branch.gold.sel{box-shadow:0 0 30px rgba(245,197,66,.3)}
.wi-gold-tag{position:absolute;top:-9px;left:12px;display:inline-flex;align-items:center;gap:4px;background:linear-gradient(135deg,#f5c542,#e8a020);color:#041E24;font-size:9.5px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;border-radius:20px;padding:3px 9px}
.wi-branch-head{display:flex;align-items:flex-start;gap:9px;margin-bottom:11px}
.wi-branch-ic{width:30px;height:30px;border-radius:9px;background:rgba(255,255,255,.06);display:flex;align-items:center;justify-content:center;flex-shrink:0}
.wi-comments{margin-left:auto;display:inline-flex;align-items:center;gap:3px;font-size:11px;color:#93B9C2}
.wi-metrics{display:grid;grid-template-columns:1fr 1fr;gap:7px}
.wi-metric{display:flex;flex-direction:column;align-items:flex-start;gap:2px;background:rgba(255,255,255,.035);border:1px solid rgba(255,255,255,.07);border-radius:9px;padding:8px 10px;font:inherit;color:inherit;cursor:pointer;text-align:left;transition:border-color .15s,background .15s}
.wi-metric:hover{border-color:rgba(34,211,238,.5);background:rgba(34,211,238,.06)}
.wi-metric.on{border-color:#22d3ee;background:rgba(34,211,238,.1)}
.wi-metric-l{font-size:10.5px;color:#7FA6B0;display:inline-flex;align-items:center;gap:5px}
.wi-metric-v{font-size:14.5px;font-weight:700;letter-spacing:-.01em}
.wi-metric-v.neg{color:#f87171}
.wi-ov-dot{width:6px;height:6px;border-radius:50%;background:#fbbf24;display:inline-block}
.wi-branch-foot{display:flex;justify-content:space-between;gap:8px;margin-top:11px;padding-top:10px;border-top:1px solid rgba(255,255,255,.07);font-size:11px;color:#7FA6B0;flex-wrap:wrap}
.wi-branch-foot b{font-weight:650}

.wi-golden{margin-top:18px;border:1px solid rgba(245,197,66,.4);border-radius:14px;background:linear-gradient(135deg,rgba(245,197,66,.09),rgba(245,197,66,.02));box-shadow:0 0 34px rgba(245,197,66,.1);padding:16px 18px;animation:wiRise .5s ease}
.wi-golden-head{display:flex;align-items:center;gap:12px;flex-wrap:wrap}
.wi-golden-badge{width:34px;height:34px;border-radius:10px;background:linear-gradient(135deg,#f5c542,#e8a020);display:flex;align-items:center;justify-content:center;box-shadow:0 0 18px rgba(245,197,66,.5);flex-shrink:0}
.wi-golden-body{font-size:12.8px;line-height:1.65;color:#BFD9DE;margin-top:11px}
.wi-golden-body b{color:#E6F3F5}
.wi-golden-chips{display:flex;gap:7px;margin-top:12px;flex-wrap:wrap}
.wi-chip{font-size:11px;font-weight:650;color:#f5c542;background:rgba(245,197,66,.1);border:1px solid rgba(245,197,66,.3);border-radius:20px;padding:4px 11px}

.wi-strip{display:grid;grid-template-columns:170px repeat(3,1fr) 1.2fr;gap:10px;margin-top:16px;border:1px solid rgba(255,255,255,.09);border-radius:13px;background:rgba(14,14,26,.9);padding:12px 14px;align-items:center}
.wi-strip-label{padding-right:6px}
.wi-tile{background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);border-radius:10px;padding:9px 12px;min-width:0}
.wi-tile-l{font-size:10px;letter-spacing:.1em;text-transform:uppercase;color:#7FA6B0;font-weight:700;margin-bottom:4px}
.wi-tile-v{font-size:16.5px;font-weight:700;letter-spacing:-.01em;transition:color .3s}
.wi-tile-d{display:inline-flex;align-items:center;gap:3px;font-size:10.5px;font-weight:650;margin-top:3px}

.wi-panel{border-left:1px solid rgba(255,255,255,.07);padding:16px;position:sticky;top:61px;max-height:calc(100vh - 120px);overflow-y:auto}
.wi-panel-head{display:flex;align-items:center;justify-content:space-between;gap:8px}
.wi-explain{border:1px solid rgba(34,211,238,.3);background:rgba(34,211,238,.05);border-radius:12px;padding:13px;margin-bottom:14px;animation:wiRise .3s ease}
.wi-formula{font-size:11.8px;color:#22d3ee;background:rgba(0,0,0,.35);border:1px solid rgba(34,211,238,.2);border-radius:8px;padding:9px 11px;line-height:1.5}
.wi-exp-row{display:flex;justify-content:space-between;align-items:center;gap:10px;padding:7px 0;border-bottom:1px solid rgba(255,255,255,.06)}
.wi-note{font-size:11.5px;color:#93B9C2;line-height:1.55;margin-top:10px}
.wi-override{margin-top:12px;padding-top:10px;border-top:1px dashed rgba(255,255,255,.12)}

.wi-field{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:7px 0;border-bottom:1px solid rgba(255,255,255,.05)}
.wi-input{display:inline-flex;align-items:center;gap:4px;background:#06303A;border:1px solid rgba(255,255,255,.13);border-radius:8px;padding:0 9px;height:30px}
.wi-input i{font-style:normal;font-size:11px;color:#7FA6B0}
.wi-input input{width:76px;background:none;border:none;outline:none;color:#E6F3F5;font:inherit;font-size:12.5px;font-weight:650;text-align:right;font-variant-numeric:tabular-nums}
.wi-input input::-webkit-outer-spin-button,.wi-input input::-webkit-inner-spin-button{-webkit-appearance:none}
.wi-input:focus-within{border-color:#22d3ee;box-shadow:0 0 10px rgba(34,211,238,.25)}

.wi-comments-list{display:flex;flex-direction:column;gap:8px}
.wi-comment{background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);border-radius:10px;padding:9px 11px;font-size:12px;color:#BFD9DE;line-height:1.5}
.wi-comment-head{display:flex;justify-content:space-between;margin-bottom:3px;font-size:11px}
.wi-comment-head b{color:#6BCEDF}
.wi-comment-head span{color:#4F757F}
.wi-comment-box{display:flex;gap:6px;margin-top:9px}
.wi-comment-box input{flex:1;background:#06303A;border:1px solid rgba(255,255,255,.13);border-radius:8px;height:32px;padding:0 11px;color:#E6F3F5;font:inherit;font-size:12px;outline:none}
.wi-comment-box input:focus{border-color:#38BDD3}

.wi-modal-bg{position:fixed;inset:0;background:rgba(4,4,10,.7);backdrop-filter:blur(4px);z-index:60;display:flex;align-items:center;justify-content:center;padding:24px}
.wi-modal{width:min(560px,100%);max-height:86vh;overflow-y:auto;background:#052A33;border:1px solid rgba(245,197,66,.35);border-radius:16px;box-shadow:0 0 60px rgba(245,197,66,.12);padding:20px;color:#E6F3F5;font-size:13px;animation:wiRise .25s ease}
.wi-step{display:flex;align-items:center;gap:11px;padding:9px 0;border-bottom:1px solid rgba(255,255,255,.06)}
.wi-step-n{width:22px;height:22px;border-radius:50%;background:rgba(245,197,66,.14);border:1px solid rgba(245,197,66,.4);color:#f5c542;font-size:11px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0}

.wi-orb-wrap{position:fixed;right:26px;bottom:30px;z-index:70;display:flex;flex-direction:column;align-items:flex-end;gap:10px}
.wi-orb{width:52px;height:52px;border-radius:50%;border:none;cursor:pointer;background:linear-gradient(135deg,#2CB3C8,#22d3ee);display:flex;align-items:center;justify-content:center;box-shadow:0 0 22px rgba(44,179,200,.55),0 4px 16px rgba(0,0,0,.4);transition:transform .15s}
.wi-orb:hover{transform:scale(1.07)}
.wi-orb.live{animation:wiOrb 1.5s infinite;background:linear-gradient(135deg,#6BCEDF,#2CB3C8)}
.wi-orb-hover{display:none;width:250px;background:#073744;border:1px solid rgba(56,189,211,.4);border-radius:13px;padding:12px 14px;font-size:11.8px;line-height:1.55;color:#BFD9DE;box-shadow:0 8px 30px rgba(0,0,0,.5)}
.wi-orb-hover b{color:#E6F3F5;display:block;margin-bottom:4px}
.wi-orb-wrap:hover .wi-orb-hover{display:block;animation:wiRise .2s ease}
.wi-suggest{width:290px;background:#073744;border:1px solid rgba(56,189,211,.5);border-radius:13px;padding:13px 14px;box-shadow:0 0 30px rgba(56,189,211,.25);animation:wiRise .3s ease}

.wi-toasts{position:fixed;left:50%;bottom:26px;transform:translateX(-50%);z-index:80;display:flex;flex-direction:column;gap:7px;align-items:center}
.wi-toast{display:inline-flex;align-items:center;gap:8px;background:#073744;border:1px solid rgba(255,255,255,.16);color:#E6F3F5;border-radius:24px;padding:8px 16px;font-size:12px;font-weight:600;box-shadow:0 8px 30px rgba(0,0,0,.5);animation:wiRise .25s ease}

@keyframes wiPulse{0%,100%{opacity:1}50%{opacity:.35}}
@keyframes wiGlow{from{box-shadow:0 0 12px rgba(44,179,200,.4)}to{box-shadow:0 0 26px rgba(44,179,200,.75),0 0 44px rgba(34,211,238,.35)}}
@keyframes wiFlow{to{stroke-dashoffset:-9}}
@keyframes wiRise{from{opacity:0;transform:translateY(7px)}to{opacity:1;transform:translateY(0)}}
@keyframes wiOrb{0%,100%{box-shadow:0 0 20px rgba(167,139,250,.6)}50%{box-shadow:0 0 38px rgba(167,139,250,.95),0 0 60px rgba(34,211,238,.4)}}
@media (max-width:1100px){.wi-body{grid-template-columns:1fr}.wi-cond{width:100%;position:static;border-right:none;border-bottom:1px solid rgba(255,255,255,.07)}.wi-panel{position:static;max-height:none;border-left:none;border-top:1px solid rgba(255,255,255,.07)}.wi-branches{grid-template-columns:1fr}.wi-strip{grid-template-columns:1fr 1fr}}
`;

export default WhatIfScreen;
